
import { useParams, useNavigate } from "react-router-dom";
import { useRegionAirports } from "@/hooks/useRegionAirports";
import { regions } from "@/types/regions";
import { useTranslation } from "@/hooks/useTranslation";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Link } from "react-router-dom";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Badge } from "@/components/ui/badge";
import { RankDisplay } from "@/components/airport/RankDisplay";
import { Progress } from "@/components/ui/progress";
import { TopAirportsCard } from "@/components/airport/TopAirportsCard";
import { StarRating } from "@/components/review/StarRating";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function RegionDetail() {
  const { regionId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const region = regions.find(r => r.id === regionId);
  const { data: airports = [], isLoading } = useRegionAirports(region?.name || '');

  // Get airports with reviews for best/worst calculation
  const airportsWithReviews = airports.filter(airport => airport.reviews.total_reviews > 0);
  
  // Sort airports by hand check success rate first, then score
  const sortedReviewedAirports = [...airportsWithReviews].sort((a, b) => {
    const rateDiff = b.reviews.hand_check_success_rate - a.reviews.hand_check_success_rate;
    if (Math.abs(rateDiff) > 0.01) return rateDiff;
    
    const scoreDiff = b.reviews.average_score - a.reviews.average_score;
    if (Math.abs(scoreDiff) > 0.01) return scoreDiff;
    
    return b.reviews.total_reviews - a.reviews.total_reviews;
  });

  // Get top 3 and bottom 3 airports with reviews
  const bestAirports = sortedReviewedAirports.slice(0, 3);
  
  // Get the worst airports and properly assign their ranks
  const worstSortedAirports = [...airportsWithReviews].sort((a, b) => {
    const rateDiff = a.reviews.hand_check_success_rate - b.reviews.hand_check_success_rate;
    if (Math.abs(rateDiff) > 0.01) return rateDiff;
    
    const scoreDiff = a.reviews.average_score - b.reviews.average_score;
    if (Math.abs(scoreDiff) > 0.01) return scoreDiff;
    
    return a.reviews.total_reviews - b.reviews.total_reviews;
  });

  // Take top 3 worst and map their actual ranks from the original list
  const worstAirports = worstSortedAirports.slice(0, 3).map(airport => {
    const actualRank = sortedReviewedAirports.findIndex(a => a.id === airport.id) + 1;
    return {
      ...airport,
      currentRank: actualRank
    };
  });

  // Sort all airports by hand check success rate first, then score
  const sortedAllAirports = [...airports].sort((a, b) => {
    if (a.reviews.total_reviews === 0 && b.reviews.total_reviews === 0) return 0;
    if (a.reviews.total_reviews === 0) return 1;
    if (b.reviews.total_reviews === 0) return -1;
    
    const rateDiff = b.reviews.hand_check_success_rate - a.reviews.hand_check_success_rate;
    if (Math.abs(rateDiff) > 0.01) return rateDiff;
    
    const scoreDiff = b.reviews.average_score - a.reviews.average_score;
    if (Math.abs(scoreDiff) > 0.01) return scoreDiff;
    
    return b.reviews.total_reviews - a.reviews.total_reviews;
  });

  if (!region) {
    return <div className="container py-8">Region not found</div>;
  }

  return (
    <div className="container py-8">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" size="icon" onClick={() => navigate('/dashboard')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-3xl font-bold">Airports in {region.name}</h1>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : airports.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">{t('no_airports_found')}</p>
        </div>
      ) : (
        <>
          {sortedReviewedAirports.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <TopAirportsCard 
                title={t('best_airports')} 
                airports={bestAirports} 
                startRank={1} 
              />
              <TopAirportsCard 
                title={t('worst_airports')} 
                airports={worstAirports} 
                startRank={1}
                isWorst={true}
              />
            </div>
          )}

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">{t('rank')}</TableHead>
                  <TableHead>{t('airport')}</TableHead>
                  <TableHead>{t('location')}</TableHead>
                  <TableHead className="text-right">{t('average_score')}</TableHead>
                  <TableHead className="text-right">{t('hand_check_success')}</TableHead>
                  <TableHead className="text-right">{t('total_reviews')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAllAirports.map((airport, index) => (
                  <TableRow key={airport.id}>
                    <TableCell className="text-center">
                      {airport.reviews.total_reviews > 0 ? (
                        <RankDisplay rank={index + 1} />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Link 
                        to={`/airport/${airport.iata_code}`}
                        className="hover:underline font-medium"
                      >
                        {airport.name}
                      </Link>
                      {airport.iata_code && (
                        <Badge variant="outline" className="ml-2">
                          {airport.iata_code}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{airport.location}</TableCell>
                    <TableCell>
                      <div className="flex justify-end items-center gap-2">
                        {airport.reviews.total_reviews > 0 ? (
                          <StarRating score={airport.reviews.average_score} />
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {airport.reviews.total_reviews > 0 ? (
                        <div className="flex items-center justify-end">
                          <Progress 
                            value={airport.reviews.hand_check_success_rate} 
                            className="w-24 h-2 mr-2"
                          />
                          <span className="w-8 text-right">
                            {airport.reviews.hand_check_success_rate.toFixed(0)}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {airport.reviews.total_reviews}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
